import {
  AdapterFormats,
  AdapterOptions,
  DateBuilderReturnType,
  FieldFormatTokenMap,
  MuiPickersAdapter,
  PickersTimezone,
} from "@mui/x-date-pickers";
import { DateTime, Info } from "ts-luxon";

declare module "@mui/x-date-pickers/models" {
  interface PickerValidDateLookup {
    "ts-luxon": DateTime;
  }
}

const formatTokenMap: FieldFormatTokenMap = {
  // Year
  y: { sectionType: "year", contentType: "digit", maxLength: 4 },
  yy: "year",
  yyyy: { sectionType: "year", contentType: "digit", maxLength: 4 },

  // Month
  L: { sectionType: "month", contentType: "digit", maxLength: 2 },
  LL: "month",
  LLL: { sectionType: "month", contentType: "letter" },
  LLLL: { sectionType: "month", contentType: "letter" },
  M: { sectionType: "month", contentType: "digit", maxLength: 2 },
  MM: "month",
  MMM: { sectionType: "month", contentType: "letter" },
  MMMM: { sectionType: "month", contentType: "letter" },

  // Day of the month
  d: { sectionType: "day", contentType: "digit", maxLength: 2 },
  dd: "day",

  // Day of the week
  c: { sectionType: "weekDay", contentType: "digit", maxLength: 1 },
  ccc: { sectionType: "weekDay", contentType: "letter" },
  cccc: { sectionType: "weekDay", contentType: "letter" },
  E: { sectionType: "weekDay", contentType: "digit", maxLength: 2 },
  EEE: { sectionType: "weekDay", contentType: "letter" },
  EEEE: { sectionType: "weekDay", contentType: "letter" },

  // Meridiem
  a: "meridiem",

  // Hours
  H: { sectionType: "hours", contentType: "digit", maxLength: 2 },
  HH: "hours",
  h: { sectionType: "hours", contentType: "digit", maxLength: 2 },
  hh: "hours",

  // Minutes
  m: { sectionType: "minutes", contentType: "digit", maxLength: 2 },
  mm: "minutes",

  // Seconds
  s: { sectionType: "seconds", contentType: "digit", maxLength: 2 },
  ss: "seconds",
};

const defaultFormats: AdapterFormats = {
  year: "yyyy",
  month: "LLLL",
  monthShort: "MMM",
  dayOfMonth: "d",
  // Full day of the month format (i.e. 3rd) is not supported
  // Falling back to regular format
  dayOfMonthFull: "d",
  weekday: "cccc",
  weekdayShort: "ccccc",
  hours24h: "HH",
  hours12h: "hh",
  meridiem: "a",
  minutes: "mm",
  seconds: "ss",

  fullDate: "DD",
  keyboardDate: "D",
  shortDate: "MMM d",
  normalDate: "d MMMM",
  normalDateWithWeekday: "EEE, MMM d",

  fullTime: "t",
  fullTime12h: "hh:mm a",
  fullTime24h: "HH:mm",

  keyboardDateTime: "D t",
  keyboardDateTime12h: "D hh:mm a",
  keyboardDateTime24h: "D T",
};

// Based on https://github.com/mui/mui-x/x-date-pickers/src/AdapterLuxon/AdapterLuxon.ts
export class MuiXAdapterTSLuxon implements MuiPickersAdapter<DateTime, string> {
  escapedCharacters = { start: "'", end: "'" };
  formats: AdapterFormats;
  formatTokenMap = formatTokenMap;
  isMUIAdapter = true;
  isTimezoneCompatible = true;
  lib = "ts-luxon";
  locale: string;

  constructor({ locale, formats }: AdapterOptions<string, never> = {}) {
    this.locale = locale || "en-GB";
    this.formats = { ...defaultFormats, ...formats };
  }

  date = <T extends string | null | undefined>(
    value?: T,
    timezone: PickersTimezone = "default"
  ): DateBuilderReturnType<T, DateTime> => {
    type R = DateBuilderReturnType<T, DateTime>;
    if (value === null) {
      return <R>null;
    }

    if (typeof value === "undefined") {
      return <R>DateTime.fromJSDate(new Date(), { locale: this.locale, zone: timezone });
    }

    return <R>DateTime.fromISO(value, { locale: this.locale, zone: timezone });
  };

  getInvalidDate = () => DateTime.invalid("Invalid date");

  getTimezone = (value: DateTime) => {
    // When using the system zone, we want to return "system", not something like "Europe/Paris"
    if (value.zone.type === "system") {
      return "system";
    }
    return value.zoneName;
  };

  setTimezone = (value: DateTime, timezone: PickersTimezone) => {
    if (!value.zone.equals(Info.normalizeZone(timezone))) {
      return value.setZone(timezone);
    }
    return value;
  };

  toJsDate = (value: DateTime): Date => {
    return value.toJSDate();
  };

  parse = (value: string, formatString: string) => {
    if (value === "") {
      return null;
    }
    return DateTime.fromFormat(value, formatString, {
      locale: this.locale,
    });
  };

  getCurrentLocaleCode = () => {
    return this.locale;
  };

  is12HourCycleInCurrentLocale = () => {
    if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat === "undefined") {
      return true; // Luxon defaults to en-US if Intl not found
    }
    return Boolean(
      new Intl.DateTimeFormat(this.locale, {
        hour: "numeric",
      })?.resolvedOptions()?.hour12
    );
  };

  expandFormat = (format: string) => {
    // Extract escaped section to avoid extending them
    const catchEscapedSectionsRegexp = /''|'(''|[^'])+('|$)|[^']*/g;

    // This RegExp tests if a string is only mad of supported tokens
    const validTokens = [...Object.keys(this.formatTokenMap), "yyyyy"];
    const isWordComposedOfTokens = new RegExp(`^(${validTokens.join("|")})+$`);

    // Extract words to test if they are a token or a word to escape.
    const catchWordsRegexp = /(?:^|[^a-z])([a-z]+)(?:[^a-z]|$)|([a-z]+)/gi;
    return (
      format
        .match(catchEscapedSectionsRegexp)!
        .map((token) => {
          const firstCharacter = token[0];
          if (firstCharacter === "'") {
            return token;
          }
          const expandedToken = DateTime.expandFormat(token, {
            locale: this.locale,
          });
          return expandedToken.replace(catchWordsRegexp, (substring, g1, g2) => {
            const word = g1 || g2; // words are either in group 1 or group 2

            if (isWordComposedOfTokens.test(word)) {
              return substring;
            }
            return `'${substring}'`;
          });
        })
        .join("")
        // The returned format can contain `yyyyy` which means year between 4 and 6 digits.
        // This value is supported by luxon parser but not luxon formatter.
        // To avoid conflicts, we replace it by 4 digits which is enough for most use-cases.
        .replace("yyyyy", "yyyy")
    );
  };

  isValid = (value: DateTime | null) => {
    if (value === null) {
      return false;
    }
    return value.isValid;
  };

  format = (value: DateTime, formatKey: string) => {
    return this.formatByString(value, this.formats[formatKey as keyof AdapterFormats]);
  };

  formatByString = (value: DateTime, format: string) => {
    return value.setLocale(this.locale).toFormat(format);
  };

  formatNumber = (numberToFormat: string) => {
    return numberToFormat;
  };

  isEqual = (value: DateTime | null, comparing: DateTime | null) => {
    if (value === null || comparing === null) {
      return value === null && comparing === null;
    }
    return +value === +comparing;
  };

  isSameYear = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    return value.hasSame(comparingInValueTimezone, "year");
  };

  isSameMonth = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    return value.hasSame(comparingInValueTimezone, "month");
  };

  isSameDay = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    return value.hasSame(comparingInValueTimezone, "day");
  };

  isSameHour = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    return value.hasSame(comparingInValueTimezone, "hour");
  };

  isAfter = (value: DateTime, comparing: DateTime) => {
    return value > comparing;
  };

  isAfterYear = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    const diff = value.diff(this.endOfYear(comparingInValueTimezone), "years").toObject();
    return diff.years! > 0;
  };

  isAfterDay = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    const diff = value.diff(this.endOfDay(comparingInValueTimezone), "days").toObject();
    return diff.days! > 0;
  };

  isBefore = (value: DateTime, comparing: DateTime) => {
    return value < comparing;
  };

  isBeforeYear = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    const diff = value.diff(this.startOfYear(comparingInValueTimezone), "years").toObject();
    return diff.years! < 0;
  };
  isBeforeDay = (value: DateTime, comparing: DateTime) => {
    const comparingInValueTimezone = this.setTimezone(comparing, this.getTimezone(value));
    const diff = value.diff(this.startOfDay(comparingInValueTimezone), "days").toObject();
    return diff.days! < 0;
  };

  isWithinRange = (value: DateTime, [start, end]: [DateTime, DateTime]) => {
    return (
      this.isEqual(value, start) ||
      this.isEqual(value, end) ||
      (this.isAfter(value, start) && this.isBefore(value, end))
    );
  };

  startOfYear = (value: DateTime) => {
    return value.startOf("year");
  };

  startOfMonth = (value: DateTime) => {
    return value.startOf("month");
  };

  startOfWeek = (value: DateTime) => {
    return this.setLocaleToValue(value).startOf("week", {
      useLocaleWeeks: true,
    });
  };

  startOfDay = (value: DateTime) => {
    return value.startOf("day");
  };
  endOfYear = (value: DateTime) => {
    return value.endOf("year");
  };
  endOfMonth = (value: DateTime) => {
    return value.endOf("month");
  };
  endOfWeek = (value: DateTime) => {
    return this.setLocaleToValue(value).endOf("week", {
      useLocaleWeeks: true,
    });
  };
  endOfDay = (value: DateTime) => {
    return value.endOf("day");
  };
  addYears = (value: DateTime, amount: number) => {
    return value.plus({
      years: amount,
    });
  };
  addMonths = (value: DateTime, amount: number) => {
    return value.plus({
      months: amount,
    });
  };
  addWeeks = (value: DateTime, amount: number) => {
    return value.plus({
      weeks: amount,
    });
  };
  addDays = (value: DateTime, amount: number) => {
    return value.plus({
      days: amount,
    });
  };
  addHours = (value: DateTime, amount: number) => {
    return value.plus({
      hours: amount,
    });
  };
  addMinutes = (value: DateTime, amount: number) => {
    return value.plus({
      minutes: amount,
    });
  };
  addSeconds = (value: DateTime, amount: number) => {
    return value.plus({
      seconds: amount,
    });
  };
  getYear = (value: DateTime) => {
    return value.year;
  };
  getMonth = (value: DateTime) => {
    // See https://github.com/moment/luxon/blob/master/docs/moment.md#major-functional-differences
    return value.month - 1;
  };
  getDate = (value: DateTime) => {
    return value.day;
  };
  getHours = (value: DateTime) => {
    return value.hour;
  };
  getMinutes = (value: DateTime) => {
    return value.minute;
  };
  getSeconds = (value: DateTime) => {
    return value.second;
  };
  getMilliseconds = (value: DateTime) => {
    return value.millisecond;
  };
  setYear = (value: DateTime, year: number) => {
    return value.set({
      year,
    });
  };
  setMonth = (value: DateTime, month: number) => {
    return value.set({
      month: month + 1,
    });
  };
  setDate = (value: DateTime, date: number) => {
    return value.set({
      day: date,
    });
  };
  setHours = (value: DateTime, hours: number) => {
    return value.set({
      hour: hours,
    });
  };
  setMinutes = (value: DateTime, minutes: number) => {
    return value.set({
      minute: minutes,
    });
  };
  setSeconds = (value: DateTime, seconds: number) => {
    return value.set({
      second: seconds,
    });
  };
  setMilliseconds = (value: DateTime, milliseconds: number) => {
    return value.set({
      millisecond: milliseconds,
    });
  };
  getDaysInMonth = (value: DateTime) => {
    return value.daysInMonth;
  };
  getWeekArray = (value: DateTime) => {
    const firstDay = this.startOfWeek(this.startOfMonth(value));
    const lastDay = this.endOfWeek(this.endOfMonth(value));

    const { days } = lastDay.diff(firstDay, "days").toObject();

    const weeks: DateTime[][] = [];
    new Array<number>(Math.round(days!))
      .fill(0)
      .map((_, i) => i)
      .map((day) => firstDay.plus({ days: day }))
      .forEach((v, i) => {
        if (i === 0 || (i % 7 === 0 && i > 6)) {
          weeks.push([v]);
          return;
        }

        weeks[weeks.length - 1].push(v);
      });

    return weeks;
  };

  getWeekNumber = (value: DateTime) => {
    return value.localWeekNumber ?? value.weekNumber;
  };
  getDayOfWeek = (value: DateTime) => {
    return value.weekday;
  };
  getYearRange = ([start, end]: [DateTime, DateTime]) => {
    const startDate = this.startOfYear(start);
    const endDate = this.endOfYear(end);
    const years = [];
    let current = startDate;
    while (this.isBefore(current, endDate)) {
      years.push(current);
      current = this.addYears(current, 1);
    }
    return years;
  };

  setLocaleToValue = (value: DateTime) => {
    const expectedLocale = this.getCurrentLocaleCode();
    if (expectedLocale === value.locale) {
      return value;
    }
    return value.setLocale(expectedLocale);
  };
}
